import {
    Component,
    inject,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { AgChartOptions, AgNodeClickEvent } from 'ag-charts-types';
import { AuthService } from '@services/auth/auth.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AgCharts } from 'ag-charts-angular';
import {
    ButtonModule,
    ButtonStyle,
    CardModule,
    DirectivesModule,
} from 'rcp-common-lib';
import { ClaimService } from '@services/claim/claim.service';
import {
    DaysToCompleteClaimsRequest,
    DaysToCompleteClaimsResponse,
    FindByDaysToCompleteRequest,
} from '@core/generated/openapi';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { Metrics } from '@core/constants/insights-metrics.constants';
import {
    AgingRoutes,
    ModuleRoutes,
} from '@core/constants/module-history.constants';
import { AgingInsightsMetricsService } from '@services/insights-metrics/aging-insights-metrics/aging-insights-metrics.service';
import { NavigationBehaviorOptions, Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { ClaimStalledFilterService } from '@services/claim-stalled-filter/claim-stalled-filter.service';
import { skip } from 'rxjs';
import { STALLED_CLAIM_FILTER_IDS } from '@core/constants/stalled-claim-filter.constants';

@UntilDestroy()
@Component({
    selector: 'rcp-oup-days-to-complete-metric',
    imports: [AgCharts, CardModule, DirectivesModule, ButtonModule],
    templateUrl: './days-to-complete-metric.component.html',
    styleUrl: './days-to-complete-metric.component.scss',
})
export class DaysToCompleteMetricComponent implements OnInit, OnChanges {
    private readonly claimStalledFilterService = inject(
        ClaimStalledFilterService,
    );

    @Input() isInsightsDetailView: boolean = false;
    @Input() detailSearchCriteria: FormGroup;
    @Input() claimStalledFilterId: string;

    isLoading: boolean = false;
    chartOptions: AgChartOptions;
    locations: number[] = [];
    fillColors = ['#2A983D', '#EEBA05', '#F79703', '#EE413E', '#A92C26'];
    fillColorIndex = 0;
    requestToResponseKeys: Map<string, string> = new Map();

    isChartInitialized: boolean = false;

    constructor(
        private authService: AuthService,
        private claimService: ClaimService,
        private insightsMetricsService: AgingInsightsMetricsService,
        private router: Router,
    ) {
        this.requestToResponseKeys.set(
            FindByDaysToCompleteRequest.DaysToCompleteEnum._030,
            'completedLessThan30ClaimCount',
        );
        this.requestToResponseKeys.set(
            FindByDaysToCompleteRequest.DaysToCompleteEnum._3145,
            'completed31to45ClaimCount',
        );
        this.requestToResponseKeys.set(
            FindByDaysToCompleteRequest.DaysToCompleteEnum._4660,
            'completed46to60ClaimCount',
        );
        this.requestToResponseKeys.set(
            FindByDaysToCompleteRequest.DaysToCompleteEnum._6190,
            'completed61to90ClaimCount',
        );
        this.requestToResponseKeys.set(
            FindByDaysToCompleteRequest.DaysToCompleteEnum.Over90,
            'completed90PlusClaimCount',
        );
    }

    ngOnInit() {
        this.getSelectedLocations();
        this.onStalledFilterChange();
    }

    ngOnChanges(changes: SimpleChanges) {
        if ('detailSearchCriteria' in changes) {
            if (this.isChartInitialized) {
                this.loadData();
            }
        }
    }

    getSelectedLocations() {
        this.authService.agingLocationFilter
            .pipe(untilDestroyed(this))
            .subscribe((locations) => {
                if (this.authService.isAgingUploadByLocationOff(locations)) {
                    this.locations = [];
                    this.loadData();
                } else if (
                    Array.isArray(locations) &&
                    !locations.every((val) => val === undefined)
                ) {
                    this.locations = locations;
                    this.loadData();
                } else if (
                    Array.isArray(locations) &&
                    locations.every((val) => val === undefined)
                ) {
                    this.loadData();
                }
            });
    }

    onStalledFilterChange() {
        if (this.claimStalledFilterId) {
            this.claimStalledFilterService
                .onFilterStatusChange(this.claimStalledFilterId)
                .pipe(untilDestroyed(this), skip(1))
                .subscribe({
                    next: () => this.loadData(),
                });
        }
    }

    loadData() {
        this.isLoading = true;
        this.fillColorIndex = 0;

        let request: DaysToCompleteClaimsRequest = this.isInsightsDetailView
            ? this.getDetailReportRequest()
            : this.getDefaultReportRequest();

        this.claimService
            .getDaysToCompleteClaims(request, !this.isInsightsDetailView)
            .pipe(untilDestroyed(this))
            .subscribe((result) => {
                this.createChart(this.setupChartData(result));
                this.isChartInitialized = true;
                this.isLoading = false;
            });
    }

    getDetailReportRequest(): DaysToCompleteClaimsRequest {
        return <DaysToCompleteClaimsRequest>{
            accountLocationIds: this.locations,
            payorIds: this.detailSearchCriteria?.controls[
                'activePayers'
            ]?.getRawValue()
                ? this.detailSearchCriteria?.controls[
                      'activePayers'
                  ]?.getRawValue()
                : [],
            dateRange:
                this.insightsMetricsService.getLocalDateRangeFromDateRangeOptions(
                    this.detailSearchCriteria,
                ),
            nonStalledClaimsOnly:
                !this.detailSearchCriteria?.controls[
                    'includeStalled'
                ].getRawValue(),
        };
    }

    getDefaultReportRequest(): DaysToCompleteClaimsRequest {
        return <DaysToCompleteClaimsRequest>{
            accountLocationIds: this.locations,
            payorIds: [],
            dateRange: {
                minDate: dayjs().subtract(91, 'days').format('YYYY-MM-DD'),
                maxDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            },
            nonStalledClaimsOnly:
                !this.claimStalledFilterService.getFilterStatusValue(
                    this.claimStalledFilterId,
                ),
        };
    }

    setupChartData(dataResponse: DaysToCompleteClaimsResponse) {
        let total = 0;
        let daysToComplete: any;

        if (this.isInsightsDetailView) {
            daysToComplete =
                this.detailSearchCriteria?.controls[
                    'daysToComplete'
                ].getRawValue();

            daysToComplete.forEach((option: any) => {
                const optionValue = option.value ? option.value : option;
                const key: string = this.requestToResponseKeys.get(optionValue);
                total =
                    total +
                    dataResponse[key as keyof DaysToCompleteClaimsResponse];
            });
        } else {
            total =
                dataResponse.completedLessThan30ClaimCount +
                dataResponse.completed31to45ClaimCount +
                dataResponse.completed46to60ClaimCount +
                dataResponse.completed61to90ClaimCount +
                dataResponse.completed90PlusClaimCount;
        }

        if (total == 0) {
            return null;
        }

        let chartData = [
            {
                label: '<30',
                count: dataResponse.completedLessThan30ClaimCount,
                percent: this.getPercent(
                    dataResponse.completedLessThan30ClaimCount,
                    total,
                ),
                filter: FindByDaysToCompleteRequest.DaysToCompleteEnum._030,
            },
            {
                label: '31-45',
                count: dataResponse.completed31to45ClaimCount,
                percent: this.getPercent(
                    dataResponse.completed31to45ClaimCount,
                    total,
                ),
                filter: FindByDaysToCompleteRequest.DaysToCompleteEnum._3145,
            },
            {
                label: '46-60',
                count: dataResponse.completed46to60ClaimCount,
                percent: this.getPercent(
                    dataResponse.completed46to60ClaimCount,
                    total,
                ),
                filter: FindByDaysToCompleteRequest.DaysToCompleteEnum._4660,
            },
            {
                label: '61-90',
                count: dataResponse.completed61to90ClaimCount,
                percent: this.getPercent(
                    dataResponse.completed61to90ClaimCount,
                    total,
                ),
                filter: FindByDaysToCompleteRequest.DaysToCompleteEnum._6190,
            },
            {
                label: '>90',
                count: dataResponse.completed90PlusClaimCount,
                percent: this.getPercent(
                    dataResponse.completed90PlusClaimCount,
                    total,
                ),
                filter: FindByDaysToCompleteRequest.DaysToCompleteEnum.Over90,
            },
        ];

        // Filter out any unselected Days To Complete segments for the detail view
        if (this.isInsightsDetailView) {
            chartData = daysToComplete.map((option: any) => {
                let optionValue = option.value ? option.value : option;
                return chartData.find((obj) => obj.filter === optionValue);
            });
        }

        return chartData;
    }

    getPercent(num: number, total: number): string {
        return Math.round((num / total) * 100).toString() + '%';
    }

    createChart(data: any) {
        if (!isNil(data)) {
            this.chartOptions = {
                data: data,
                series: [
                    {
                        type: 'donut',
                        angleKey: 'count',
                        sectorLabelKey: 'percent',
                        calloutLabelKey: 'label',
                        legendItemKey: 'label',
                        innerRadiusRatio: 0.5,
                        itemStyler: (params) => {
                            const colorIndex =
                                this.fillColorIndex % this.fillColors.length;
                            params.fill = this.fillColors[colorIndex];
                            this.fillColorIndex++;
                            return params;
                        },
                        tooltip: {
                            renderer: ({ datum }) => {
                                return {
                                    title: `${datum['count'].toLocaleString('en-US')} Claims, ${datum['percent']}`,
                                    heading: `${datum['label']} Days`,
                                    data: [],
                                };
                            },
                        },
                        calloutLine: {
                            colors: ['black'],
                        },
                        listeners: {
                            nodeClick: (params) => this.handleClick(params),
                        },
                    },
                ],
            };
        } else {
            this.chartOptions = undefined;
        }
    }

    handleClick(params: AgNodeClickEvent<'nodeClick', any>) {
        if (!this.isInsightsDetailView) {
            this.insightsMetricsService.selectedAgingMetric.next(
                Metrics.DaysToComplete,
            );

            let filter = params.datum['filter'];

            this.setDetailStalledFilter();

            this.router.navigateByUrl(
                ModuleRoutes.Aging + '/' + AgingRoutes.Insights + '/detail',
                <NavigationBehaviorOptions>{
                    state: {
                        filter: filter,
                    },
                },
            );
        }
    }

    routeToReport() {
        this.insightsMetricsService.selectedAgingMetric.next(
            Metrics.DaysToComplete,
        );
        this.setDetailStalledFilter();
        this.router.navigateByUrl(
            ModuleRoutes.Aging + '/' + AgingRoutes.Insights + '/detail',
            <NavigationBehaviorOptions>{
                state: {
                    fromViewDetails: true,
                },
            },
        );
    }

    setDetailStalledFilter() {
        // Update detail filter to match current filter
        this.claimStalledFilterService.updateFilterStatus(
            STALLED_CLAIM_FILTER_IDS.INSIGHTS_DAYS_TO_COMPLETE,
            this.claimStalledFilterService.getFilterStatusValue(
                this.claimStalledFilterId,
            ),
        );
    }

    protected readonly ButtonStyle = ButtonStyle;
}
